import React from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { ScoringSettings } from './ScoringSettings';
import { CURRENCIES, SALARY_PERIODS, usesLakhInput, toAbsolute, toInputUnit, inputHint } from '../../lib/salary';
import { RichTextEditor } from '../RichTextEditor';

const inputStyle = {
  width: '100%', padding: '0.625rem 0.875rem', borderRadius: 10,
  border: '1px solid #CBD5E1', background: '#FAF7F2',
  color: '#002A38', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box'
};

const labelStyle = { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '0.375rem' };
const benchLabelStyle = { display: 'block', fontSize: '0.6875rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' };

// Edit Job dialog (title/description/skills + per-stage benchmark scores)
export function EditJobDialog({ open, onOpenChange, editForm, setEditForm, onSubmit }) {
  const cur = editForm.salary_currency || 'USD';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ background: '#fff', border: '1px solid #CBD5E1', borderRadius: 14, maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Cabinet Grotesk' }}>Edit Job</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={labelStyle}>Title</label>
            <input value={editForm.title || ''} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Description</label>
            <RichTextEditor testId="edit-job-description-input" value={editForm.description || ''} onChange={(html) => setEditForm({ ...editForm, description: html })} placeholder="Describe the role, responsibilities, and requirements…" />
          </div>
          <div>
            <label style={labelStyle}>Required Skills (comma-separated)</label>
            <input value={editForm.required_skills || ''} onChange={(e) => setEditForm({ ...editForm, required_skills: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Salary Range (optional){usesLakhInput(cur) ? ' — enter in ₹ Lakhs' : ''}</label>
            <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr 100px', gap: '0.75rem' }}>
              <select data-testid="edit-salary-currency" value={cur} onChange={(e) => setEditForm({ ...editForm, salary_currency: e.target.value })} style={inputStyle}>
                {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select>
              <input data-testid="edit-salary-min" type="number" min="0" step={usesLakhInput(cur) ? '0.01' : '1'} value={usesLakhInput(cur) ? toInputUnit(editForm.salary_min, cur) : (editForm.salary_min ?? '')} onChange={(e) => setEditForm({ ...editForm, salary_min: toAbsolute(e.target.value, cur) })} placeholder={usesLakhInput(cur) ? 'Min (L)' : 'Min'} style={inputStyle} />
              <input data-testid="edit-salary-max" type="number" min="0" step={usesLakhInput(cur) ? '0.01' : '1'} value={usesLakhInput(cur) ? toInputUnit(editForm.salary_max, cur) : (editForm.salary_max ?? '')} onChange={(e) => setEditForm({ ...editForm, salary_max: toAbsolute(e.target.value, cur) })} placeholder={usesLakhInput(cur) ? 'Max (L)' : 'Max'} style={inputStyle} />
              <select data-testid="edit-salary-period" value={editForm.salary_period || 'year'} onChange={(e) => setEditForm({ ...editForm, salary_period: e.target.value })} style={inputStyle}>
                {SALARY_PERIODS.map((p) => <option key={p.value} value={p.value}>{p.short}</option>)}
              </select>
            </div>
            {usesLakhInput(cur) && (editForm.salary_min || editForm.salary_max) && (
              <div data-testid="edit-salary-lakh-hint" style={{ fontSize: '0.6875rem', color: '#0F766E', marginTop: '0.35rem', fontWeight: 600 }}>
                ≈ {inputHint(toInputUnit(editForm.salary_min, cur), cur) || '—'} – {inputHint(toInputUnit(editForm.salary_max, cur), cur) || '—'}
              </div>
            )}
          </div>
          <div style={{ borderTop: '1px solid #ECE7DE', paddingTop: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.25rem' }}>Stage Benchmark Scores</label>
            <p style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.625rem' }}>Minimum score (0&ndash;100) to be eligible to advance into each stage. Recruiters always decide; no auto-advance.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
              <div>
                <label style={benchLabelStyle}>Screening</label>
                <input data-testid="edit-benchmark-screening" type="number" min="0" max="100" value={editForm.benchmark_screening ?? 60} onChange={(e) => setEditForm({ ...editForm, benchmark_screening: parseInt(e.target.value) || 0 })} style={inputStyle} />
              </div>
              <div>
                <label style={benchLabelStyle}>Assessment</label>
                <input data-testid="edit-benchmark-assessment" type="number" min="0" max="100" value={editForm.benchmark_assessment ?? 70} onChange={(e) => setEditForm({ ...editForm, benchmark_assessment: parseInt(e.target.value) || 0 })} style={inputStyle} />
              </div>
              <div>
                <label style={benchLabelStyle}>Interview</label>
                <input data-testid="edit-benchmark-interview" type="number" min="0" max="100" value={editForm.benchmark_interview ?? 75} onChange={(e) => setEditForm({ ...editForm, benchmark_interview: parseInt(e.target.value) || 0 })} style={inputStyle} />
              </div>
              <div>
                <label style={benchLabelStyle}>BGV / Offer</label>
                <input data-testid="edit-benchmark-bgv" type="number" min="0" max="100" value={editForm.benchmark_bgv ?? 75} onChange={(e) => setEditForm({ ...editForm, benchmark_bgv: parseInt(e.target.value) || 0 })} style={inputStyle} />
              </div>
            </div>
          </div>
          <ScoringSettings form={editForm} setForm={setEditForm} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} style={{ borderRadius: 10 }}>Cancel</Button>
            <Button type="submit" style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600 }}>Save Changes</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
